/**
 * The evolution cost curve, with whatever constants the selected branch is made
 * of on controls.
 *
 * Two shapes, because the branches are two shapes:
 *
 *   **loop** — live. The price of a point is a line times a compounding
 *   surcharge, summed by `calc_feed_effects` one iteration at a time, and the
 *   two constants of that price are the sliders.
 *
 *   **power** — the 3v3 test and the tuning scenario. No loop at all:
 *   `E = (M/S)^p`, two constants and an elementary inverse.
 *
 * Everything *around* the shape is deliberately identical between them: the
 * same window, the same two sliders and an action slot, the same four
 * statistics measuring the same four things, and the same reference-curve
 * toggle. A branch switch should change what the curve does, not where to look
 * for anything — so the only things that move are the numbers and the labels.
 *
 * The window is the app's own zoom, the same state the charts page and the
 * threat illustrator run on. That settles what would otherwise be an arbitrary
 * choice — the tail past 100% is the interesting part on the power-law
 * branches and dead flat on live, so no single fixed window suits all three —
 * and hands it to the reader instead. Scroll, +/−, the preset list and
 * double-click to reset all work here exactly as they do on a chart.
 *
 * On the scenario the sliders are not a local what-if: they are the scenario's
 * own constants, so moving one here moves every chart in the app.
 *
 * The plot is hand-rolled rather than a `ChartFrame`: that shell carries the
 * shared cursor and selection as well, and this page wants only the domain.
 */

import { useMemo, useState } from 'react';

import { ParentSize } from '@visx/responsive';

import {
  DEFAULT_SCENARIO_PARAMS,
  EVO_POWER_MAX,
  EVO_POWER_MIN,
  EVO_POWER_STEP,
  GAME_COST,
  TESTING_FEED,
  costClosedCurve,
  costClosedEvo,
  costClosedMutagen,
  costCrossoverEvo,
  costDoublingPoints,
  costInflationPerPoint,
  costLoopCurve,
  costLoopEvo,
  costLoopMutagen,
  costPointIndex,
  costPointPrice,
  formatCompact,
  formatEvo,
  isGameCost,
  scenarioEvoForMutagen,
  scenarioMutagenForEvo,
  testingEvoForMutagen,
  testingMutagenForEvo,
  toDisplayMutagen,
  type CostParams,
  type CostPoint,
  type ScenarioParams,
} from '../../model';
import { ParamActions, ParamRow, ParamSlider } from '../ParamRow';
import { ScenarioReset } from '../ScenarioPanel';
import { ZoomControls } from '../ZoomControls';
import { SegmentedControl, Stat } from '../controls';
import { useLab } from '../LabContext';
import { evoTicks } from '../ticks';
import { useWheelZoom } from '../useWheelZoom';

/**
 * The closed form is only defined to 100%: `costPointIndex` clamps there, so
 * past it the integral stops moving. Its curve therefore ends at the cap rather
 * than being extrapolated into a vertical line.
 */
const CLOSED_MAX_EVO = 1;

/** Where the doubling statistic is measured, for a curve that has no single one. */
const DOUBLE_FROM = 0.5;

const GAME_COLOUR = '#7fd1ff';
const YOURS_COLOUR = '#ffb457';
const CLOSED_COLOUR = '#b490ff';
const TESTING_COLOUR = '#68dd9c';

const MARGIN = { top: 12, right: 16, bottom: 26, left: 48 };
const PLOT_HEIGHT = 250;

/** Presets. Each one switches a factor off, or leans on it, and says so. */
const PRESETS: { label: string; title: string; params: CostParams }[] = [
  { label: 'Game', title: 'The scenario as it ships', params: GAME_COST },
  {
    label: 'Line only',
    title: 'No compounding: the price rises in proportion to how far you are, and no more',
    params: { linear: 1, compounding: 0 },
  },
  {
    label: 'Compounding only',
    title: 'No line: a flat percentage surcharge per point, from the first one',
    params: { linear: 0, compounding: 0.015 },
  },
  {
    label: 'Harsh',
    title: 'Twice the compounding — a much harder ceiling',
    params: { linear: 1, compounding: 0.03 },
  },
];

/** The two constants of a power-law branch, and what it does past 100%. */
export interface PowerParams {
  /** `S` — mutagen that buys exactly 100% evolution. */
  scale: number;
  /** `p` in `E = (M / S) ^ p`. */
  power: number;
  /** True: the curve follows its own tangent past 100%. */
  stopAt100: boolean;
}

const TESTING_POWER: PowerParams = {
  scale: TESTING_FEED.evoScale,
  power: TESTING_FEED.evoPower,
  stopAt100: false,
};

/**
 * The power law a branch is running, for the two consumers that need it as a
 * pair of numbers rather than as a ruleset: this explorer, and the prose above
 * it. Live has no such pair — it is a loop — and callers check that first.
 */
export function powerParamsFor(rulesetId: string, scenario: ScenarioParams): PowerParams {
  return rulesetId === 'scenario'
    ? {
        scale: scenario.evoMutagenAt100,
        power: scenario.evoPower,
        stopAt100: scenario.evoStopScalingAt100,
      }
    : TESTING_POWER;
}

/**
 * A power-law pair as a full parameter set, so the model's own evolution
 * functions can be used rather than the algebra being written a second time.
 * Only the three evolution fields are read by them; the rest are along for the
 * ride.
 */
const asScenario = (p: PowerParams): ScenarioParams => ({
  ...DEFAULT_SCENARIO_PARAMS,
  evoMutagenAt100: p.scale,
  evoPower: p.power,
  evoStopScalingAt100: p.stopAt100,
});

const samePower = (a: PowerParams, b: PowerParams) =>
  a.scale === b.scale && a.power === b.power && a.stopAt100 === b.stopAt100;

/** Nice-ish tick values across [0, hi], including both ends. */
function linearTicks(hi: number, count = 5): number[] {
  return Array.from({ length: count + 1 }, (_, i) => (hi * i) / count);
}

/** One decade per tick, for the log axis. */
function logTicks(lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let e = Math.ceil(Math.log10(lo)); 10 ** e <= hi; e += 1) out.push(10 ** e);
  return out;
}

interface Curve {
  key: string;
  label: string;
  colour: string;
  dashed?: boolean;
  /** Drawn behind the others, as a reference rather than as an answer. */
  faint?: boolean;
  points: CostPoint[];
  /** Exact evolution at a mutagen value — never read off the sampled points. */
  evoAt: (mutagen: number) => number;
}

/** Where a curve stops, so the cursor does not report a value it never had. */
const curveEnd = (curve: Curve) => curve.points[curve.points.length - 1].mutagen;

/** Evenly spaced evolution samples of a power law, as plottable points. */
function powerCurve(params: PowerParams, maxEvo: number): CostPoint[] {
  const full = asScenario(params);
  return Array.from({ length: 121 }, (_, i) => {
    const evo = (maxEvo * i) / 120;
    return { mutagen: scenarioMutagenForEvo(evo, full), evo };
  });
}

/** The loop's price of the point being bought at `evo`. */
const loopPointPrice = (evo: number, p: CostParams) => costPointPrice(costPointIndex(evo), p);

export function EvolutionExplorer() {
  const { ruleset, scenario, setScenario, resetScenario, maxEvo, zoomBy, resetZoom } = useLab();
  const mode = ruleset.id === 'live' ? 'loop' : 'power';
  const svgRef = useWheelZoom(zoomBy);

  // Loop mode's two constants, and power mode's local what-if. Both are held
  // even when the other mode is showing: switching branch and switching back
  // should not silently reset what you were looking at.
  const [params, setParams] = useState<CostParams>(GAME_COST);
  const [yours, setYours] = useState<PowerParams>(TESTING_POWER);
  const [showClosed, setShowClosed] = useState(true);
  const [showTesting, setShowTesting] = useState(false);
  const [showLive, setShowLive] = useState(true);
  const [xScaleType, setXScaleType] = useState<'linear' | 'log'>('linear');
  const [hover, setHover] = useState<number | null>(null);

  const custom = !isGameCost(params);

  /**
   * The branch's own power law. On the scenario these are the live constants,
   * so this curve *is* the thing the sliders move; on the 3v3 test they are
   * frozen and the local pair below is what moves.
   */
  const branchPower = useMemo(() => powerParamsFor(ruleset.id, scenario), [ruleset.id, scenario]);
  const scenarioBranch = ruleset.id === 'scenario';
  const movedFromBranch = !scenarioBranch && !samePower(yours, branchPower);
  /** The power law the stats and the formula line describe. */
  const shown = movedFromBranch ? yours : branchPower;

  const curves = useMemo<Curve[]>(() => {
    if (mode === 'power') {
      const list: Curve[] = [];
      if (showLive) {
        list.push({
          key: 'live',
          label: 'Live — the feeding loop',
          colour: GAME_COLOUR,
          faint: true,
          points: costLoopCurve(GAME_COST, maxEvo),
          evoAt: (m) => costLoopEvo(m, GAME_COST),
        });
      }
      const branchFull = asScenario(branchPower);
      list.push({
        key: 'branch',
        label: `${ruleset.label} — E = (M/S)^p`,
        colour: scenarioBranch ? YOURS_COLOUR : TESTING_COLOUR,
        points: powerCurve(branchPower, maxEvo),
        evoAt: (m) => scenarioEvoForMutagen(m, branchFull),
      });
      if (movedFromBranch) {
        const yoursFull = asScenario(yours);
        list.push({
          key: 'yours',
          label: 'Yours — the same shape, moved',
          colour: YOURS_COLOUR,
          dashed: true,
          points: powerCurve(yours, maxEvo),
          evoAt: (m) => scenarioEvoForMutagen(m, yoursFull),
        });
      }
      return list;
    }

    const list: Curve[] = [
      {
        key: 'game',
        label: custom ? 'Game (unchanged)' : 'Game — the live loop',
        colour: GAME_COLOUR,
        faint: custom,
        points: costLoopCurve(GAME_COST, maxEvo),
        evoAt: (m) => costLoopEvo(m, GAME_COST),
      },
    ];
    if (custom) {
      list.push({
        key: 'yours',
        label: 'Yours — the same loop',
        colour: YOURS_COLOUR,
        points: costLoopCurve(params, maxEvo),
        evoAt: (m) => costLoopEvo(m, params),
      });
    }
    if (showClosed) {
      list.push({
        key: 'closed',
        label: custom ? 'Closed form of yours' : 'Closed form',
        colour: CLOSED_COLOUR,
        dashed: true,
        points: costClosedCurve(params, CLOSED_MAX_EVO),
        evoAt: (m) => costClosedEvo(m, params, CLOSED_MAX_EVO),
      });
    }
    if (showTesting) {
      list.push({
        key: 'testing',
        label: '3v3 branch — a plain power law',
        colour: TESTING_COLOUR,
        dashed: true,
        points: Array.from({ length: 121 }, (_, i) => {
          const evo = (maxEvo * i) / 120;
          return { mutagen: testingMutagenForEvo(evo), evo };
        }),
        evoAt: (m) => Math.min(testingEvoForMutagen(m), maxEvo),
      });
    }
    return list;
  }, [
    mode,
    maxEvo,
    params,
    custom,
    showClosed,
    showTesting,
    showLive,
    branchPower,
    scenarioBranch,
    yours,
    movedFromBranch,
    ruleset.label,
  ]);

  /** Wide enough to hold every drawn curve's last point. */
  const domainHi = Math.max(...curves.map(curveEnd), 1e-6);
  /** The first point of the game curve, so the log axis starts where prices do. */
  const logLo = Math.max(domainHi * 1e-5, 1e-9);

  return (
    <div className="evo-lab">
      <ParamRow>
        {mode === 'loop' ? (
          <LoopControls params={params} setParams={setParams} />
        ) : (
          <PowerControls
            value={shown}
            branch={branchPower}
            scenarioBranch={scenarioBranch}
            onChange={(patch) => {
              if (scenarioBranch) {
                setScenario({
                  ...(patch.scale === undefined ? {} : { evoMutagenAt100: patch.scale }),
                  ...(patch.power === undefined ? {} : { evoPower: patch.power }),
                  ...(patch.stopAt100 === undefined
                    ? {}
                    : { evoStopScalingAt100: patch.stopAt100 }),
                });
              } else {
                setYours((current) => ({ ...current, ...patch }));
              }
            }}
            onReset={() => (scenarioBranch ? resetScenario() : setYours(branchPower))}
            atBranch={scenarioBranch ? undefined : !movedFromBranch}
          />
        )}
      </ParamRow>

      {/* What the sliders currently say, and the window it is drawn in. Same
          row on every branch, the same way the illustrator carries its own. */}
      <div className="plot-head">
        <p className="plot-formula formula">
          {mode === 'loop' ? (
            <>
              price of the n-th evolution point = (1 + {fmt(params.linear)}·(n−1)) × 10^(
              {fmt(params.compounding, 4)}·n) / 200
            </>
          ) : (
            <>
              E = (M / {fmt(shown.scale, 2)}) ^ {fmt(shown.power)}
              {shown.stopAt100 ? ', tangent past 100%' : ', power law throughout'}
            </>
          )}
        </p>
        <div className="plot-zoom">
          <ZoomControls />
        </div>
      </div>

      {mode === 'loop' ? (
        <LoopStats params={params} custom={custom} />
      ) : (
        <PowerStats params={shown} />
      )}

      <ParentSize className="evo-chart">
        {({ width }) => {
          if (width <= 0) return null;
          const innerWidth = Math.max(width - MARGIN.left - MARGIN.right, 10);

          const x = (m: number) => {
            if (xScaleType === 'log') {
              const lo = Math.log(logLo);
              const hi = Math.log(domainHi);
              return ((Math.log(Math.max(m, logLo)) - lo) / (hi - lo)) * innerWidth;
            }
            return (m / domainHi) * innerWidth;
          };
          const unx = (px: number) => {
            const t = Math.min(Math.max(px / innerWidth, 0), 1);
            if (xScaleType === 'log') {
              const lo = Math.log(logLo);
              return Math.exp(lo + t * (Math.log(domainHi) - lo));
            }
            return t * domainHi;
          };
          const y = (evo: number) => PLOT_HEIGHT - (evo / maxEvo) * PLOT_HEIGHT;

          const path = (points: CostPoint[]) =>
            points
              .map(
                (pt, i) =>
                  `${i === 0 ? 'M' : 'L'}${x(pt.mutagen).toFixed(2)} ${y(pt.evo).toFixed(2)}`,
              )
              .join(' ');

          const xTicks =
            xScaleType === 'log' ? logTicks(logLo, domainHi) : linearTicks(domainHi, 5);
          const crossover = mode === 'loop' ? costCrossoverEvo(params) : null;
          const crossoverMutagen = crossover === null ? 0 : costLoopMutagen(crossover, params);
          /** Curves the cursor is actually on — the closed form ends early. */
          const atCursor = hover === null ? [] : curves.filter((c) => hover <= curveEnd(c) * 1.001);

          return (
            <>
              <svg
                ref={svgRef}
                width={width}
                height={PLOT_HEIGHT + MARGIN.top + MARGIN.bottom}
                role="img"
                aria-label="Evolution against mutagen"
                onDoubleClick={resetZoom}
              >
                <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                  {evoTicks(maxEvo).map((evo) => (
                    <g key={evo}>
                      <line x1={0} x2={innerWidth} y1={y(evo)} y2={y(evo)} stroke="#212836" />
                      <text x={-8} y={y(evo) + 3.5} textAnchor="end" className="wave-axis-text">
                        {formatEvo(evo, 0)}
                      </text>
                    </g>
                  ))}

                  {xTicks.map((m) => (
                    <text
                      key={m}
                      x={x(m)}
                      y={PLOT_HEIGHT + 15}
                      textAnchor={m === xTicks[0] ? 'start' : 'middle'}
                      className="wave-axis-text"
                    >
                      {formatCompact(toDisplayMutagen(m), 1)}
                    </text>
                  ))}

                  {/* 100% is where every branch changes character — the loop
                      freezes its price, the scenario joins its tangent — so it
                      is a landmark rather than one more gridline. */}
                  <g className="evo-cap">
                    <line x1={0} x2={innerWidth} y1={y(1)} y2={y(1)} />
                  </g>

                  {/* Where the line stops driving the price and the compounding
                      takes over — the one landmark the curve itself never shows. */}
                  {crossover !== null ? (
                    <g className="evo-crossover">
                      <line
                        x1={x(crossoverMutagen)}
                        x2={x(crossoverMutagen)}
                        y1={0}
                        y2={PLOT_HEIGHT}
                      />
                      <text x={x(crossoverMutagen) + 4} y={11}>
                        {formatEvo(crossover, 0)} · factors tie
                      </text>
                    </g>
                  ) : null}

                  {curves.map((curve) => (
                    <path
                      key={curve.key}
                      d={path(curve.points)}
                      fill="none"
                      stroke={curve.colour}
                      strokeWidth={2}
                      strokeDasharray={curve.dashed ? '5 4' : undefined}
                      strokeOpacity={curve.faint ? 0.55 : 1}
                    />
                  ))}

                  {hover !== null ? (
                    <g>
                      <line
                        x1={x(hover)}
                        x2={x(hover)}
                        y1={0}
                        y2={PLOT_HEIGHT}
                        stroke="#8b97ad"
                        strokeDasharray="3 3"
                      />
                      {atCursor.map((curve) => (
                        <circle
                          key={curve.key}
                          cx={x(hover)}
                          cy={y(Math.min(curve.evoAt(hover), maxEvo))}
                          r={3.5}
                          fill={curve.colour}
                          stroke="#141922"
                          strokeWidth={1.5}
                        />
                      ))}
                    </g>
                  ) : null}

                  <line x1={0} x2={innerWidth} y1={PLOT_HEIGHT} y2={PLOT_HEIGHT} stroke="#2c3444" />
                  <rect
                    x={0}
                    y={0}
                    width={innerWidth}
                    height={PLOT_HEIGHT}
                    fill="transparent"
                    onMouseMove={(event) => {
                      const bounds = event.currentTarget.getBoundingClientRect();
                      setHover(unx(event.clientX - bounds.left));
                    }}
                    onMouseLeave={() => setHover(null)}
                  />
                </g>
                <text x={4} y={MARGIN.top - 3} className="wave-axis-text">
                  evo
                </text>
              </svg>

              {hover !== null ? (
                <div
                  className="wave-tip"
                  style={{
                    left: MARGIN.left + x(hover),
                    transform:
                      MARGIN.left + x(hover) > width * 0.62
                        ? 'translateX(calc(-100% - 14px))'
                        : 'translateX(14px)',
                  }}
                >
                  <b className="wave-tip-head">
                    {formatCompact(toDisplayMutagen(hover), 2)} mutagen fed
                  </b>
                  {atCursor.map((curve) => (
                    <span key={curve.key}>
                      <i className="evo-tip-mark" style={{ background: curve.colour }} />
                      {curve.label.split(' —')[0]} <b>{formatEvo(curve.evoAt(hover))}</b>
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          );
        }}
      </ParentSize>

      <div className="evo-legend">
        {curves.map((curve) => (
          <span key={curve.key}>
            <i
              className="evo-legend-mark"
              style={{
                borderTopColor: curve.colour,
                borderTopStyle: curve.dashed ? 'dashed' : 'solid',
              }}
            />
            {curve.label}
          </span>
        ))}
        <span className="evo-controls">
          {mode === 'loop' ? (
            <>
              <label>
                <input
                  type="checkbox"
                  checked={showClosed}
                  onChange={(event) => setShowClosed(event.target.checked)}
                />
                Closed form
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showTesting}
                  onChange={(event) => setShowTesting(event.target.checked)}
                />
                3v3 branch
              </label>
            </>
          ) : (
            <label>
              <input
                type="checkbox"
                checked={showLive}
                onChange={(event) => setShowLive(event.target.checked)}
              />
              Live loop
            </label>
          )}
          <SegmentedControl
            value={xScaleType}
            onChange={setXScaleType}
            options={[
              { value: 'linear', label: 'Lin', title: 'Linear mutagen axis' },
              { value: 'log', label: 'Log', title: 'Logarithmic mutagen axis' },
            ]}
          />
        </span>
      </div>

      <p className="evo-note doc-note">
        The x axis is total mutagen fed from evolution 0, in the game's own strength units, and
        rescales itself to whichever curve is dearest. The y axis is the app's shared zoom — scroll
        the plot, or use the control above it, and double-click to reset. Difficulty never enters:
        it converts flasks into mutagen, not mutagen into evolution.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------- loop mode */

function LoopControls({
  params,
  setParams,
}: {
  params: CostParams;
  setParams: (update: (p: CostParams) => CostParams) => void;
}) {
  const inflation = costInflationPerPoint(params);
  const doubling = costDoublingPoints(params);
  return (
    <>
      <ParamSlider
        label="Linear growth"
        hint="a — what the multiplier gains per evolution point"
        value={params.linear}
        min={0}
        max={3}
        step={0.05}
        format={(v) => `a = ${v.toFixed(2)}`}
        onChange={(linear) => setParams((p) => ({ ...p, linear }))}
        note={
          params.linear > 0
            ? `point 101 carries ×${(1 + params.linear * 100).toFixed(0)}`
            : 'switched off'
        }
      />
      <ParamSlider
        label="Compounding"
        hint="k — the base-10 exponent added per evolution point"
        value={params.compounding}
        min={0}
        max={0.04}
        step={0.0005}
        format={(v) => `k = ${v.toFixed(4)}`}
        onChange={(compounding) => setParams((p) => ({ ...p, compounding }))}
        note={
          inflation > 0
            ? `+${(inflation * 100).toFixed(2)}% per point · ×2 every ${doubling.toFixed(0)}`
            : 'switched off'
        }
      />
      <ParamActions>
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            title={preset.title}
            className={
              preset.params.linear === params.linear &&
              preset.params.compounding === params.compounding
                ? 'active'
                : undefined
            }
            onClick={() => setParams(() => preset.params)}
          >
            {preset.label}
          </button>
        ))}
      </ParamActions>
    </>
  );
}

function LoopStats({ params, custom }: { params: CostParams; custom: boolean }) {
  const firstPrice = loopPointPrice(0, params);
  const lastPrice = loopPointPrice(0.99, params);
  const doubled = costLoopEvo(2 * costLoopMutagen(DOUBLE_FROM, params), params);
  // Past the cap `u` is frozen, so every further point costs the same and a
  // whole +100% is a hundred of them.
  const pastCap = loopPointPrice(1, params) * 100;
  return (
    <div className="evo-stats">
      <Stat
        label="Mutagen to 100%"
        value={formatCompact(toDisplayMutagen(costLoopMutagen(1, params)), 1)}
        sub="summed by the loop"
        tone={custom ? 'accent' : undefined}
      />
      <Stat
        label="Last point vs first"
        value={firstPrice > 0 ? `${formatCompact(lastPrice / firstPrice, 1)}×` : '—'}
        sub={`${formatCompact(toDisplayMutagen(firstPrice), 0)} ⟶ ${formatCompact(
          toDisplayMutagen(lastPrice),
          0,
        )}`}
      />
      <Stat
        label="Double the mutagen"
        value={`×${(doubled / DOUBLE_FROM).toFixed(3)}`}
        sub="evolution, from 50% — it varies"
      />
      <Stat
        label="Past 100%"
        value={`${formatCompact(toDisplayMutagen(pastCap), 1)} / +100%`}
        sub="flat — u clamps and the price freezes"
      />
    </div>
  );
}

/* ------------------------------------------------------------ power mode */

/**
 * The same two sliders and action slot as the loop's, so the control block does
 * not move when the branch does. On the scenario they write to the scenario
 * itself; on the 3v3 test, whose constants are frozen, they drive a local
 * what-if curve instead.
 */
function PowerControls({
  value,
  branch,
  scenarioBranch,
  onChange,
  onReset,
  atBranch,
}: {
  value: PowerParams;
  branch: PowerParams;
  scenarioBranch: boolean;
  onChange: (patch: Partial<PowerParams>) => void;
  onReset: () => void;
  /** Whether the local pair still matches the branch. Undefined on the scenario. */
  atBranch?: boolean;
}) {
  return (
    <>
      <ParamSlider
        label="Mutagen at 100%"
        hint="S — what a full run of evolution costs"
        value={value.scale}
        min={20}
        max={1200}
        step={1}
        format={(v) => `S = ${v.toFixed(0)}`}
        onChange={(scale) => onChange({ scale })}
        note={
          value.scale === branch.scale
            ? 'the branch as it ships'
            : `branch charges ${branch.scale.toFixed(2)}`
        }
      />
      <ParamSlider
        label="Scaling factor"
        hint="p — the diminishing return itself"
        value={value.power}
        min={EVO_POWER_MIN}
        max={EVO_POWER_MAX}
        step={EVO_POWER_STEP}
        format={(v) => `p = ${v.toFixed(2)}`}
        onChange={(power) => onChange({ power })}
        note={`double the mutagen, ×${(2 ** value.power).toFixed(3)} the evolution`}
      />
      <ParamActions>
        {scenarioBranch ? (
          <>
            <label className="param-check">
              <input
                type="checkbox"
                checked={value.stopAt100}
                onChange={(event) => onChange({ stopAt100: event.target.checked })}
              />
              Stop scaling at 100%
            </label>
            <ScenarioReset />
          </>
        ) : (
          <button
            type="button"
            title="Back to the branch's own constants"
            className={atBranch ? 'active' : undefined}
            onClick={onReset}
          >
            Branch
          </button>
        )}
      </ParamActions>
    </>
  );
}

function PowerStats({ params }: { params: PowerParams }) {
  const full = asScenario(params);
  const mutagenTo = (evo: number) => scenarioMutagenForEvo(evo, full);
  /** What one more evolution point costs, at `evo`. */
  const pointPrice = (evo: number) => mutagenTo(evo + 0.01) - mutagenTo(evo);
  const firstPrice = pointPrice(0);
  const lastPrice = pointPrice(0.99);
  return (
    <div className="evo-stats">
      <Stat
        label="Mutagen to 100%"
        value={formatCompact(toDisplayMutagen(params.scale), 1)}
        sub="S, by definition"
      />
      <Stat
        label="Last point vs first"
        value={firstPrice > 0 ? `${formatCompact(lastPrice / firstPrice, 1)}×` : '—'}
        sub={`${formatCompact(toDisplayMutagen(firstPrice), 0)} ⟶ ${formatCompact(
          toDisplayMutagen(lastPrice),
          0,
        )}`}
      />
      <Stat
        label="Double the mutagen"
        value={`×${(2 ** params.power).toFixed(3)}`}
        sub="evolution, at every level"
      />
      <Stat
        label="Past 100%"
        value={
          params.stopAt100
            ? `${formatCompact(toDisplayMutagen(params.scale / params.power), 1)} / +100%`
            : 'no cap'
        }
        sub={params.stopAt100 ? 'flat, on the tangent' : 'the power law keeps charging more'}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/** Trim a slider value to the shortest form that still reads exactly. */
function fmt(value: number, digits = 2): string {
  return Number(value.toFixed(digits)).toString();
}

/** Kept for the closed-form figure the explorer's prose quotes. */
export const closedFormPremium = (p: CostParams): number =>
  costClosedMutagen(1, p) / costLoopMutagen(1, p) - 1;
